import { useCallback } from "react"
import { useController, useFormContext } from "react-hook-form"
import { useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { MultiSelect } from "./MultiSelect"

export interface SelectOption {
	hexCode?: string
	label: string
	value: string
}

export interface SelectOptionGroup {
	label: string
	options: SelectOption[]
}

export interface MultiSelectFieldProps {
	disabled?: boolean
	disabledHint?: string
	interceptChange?: (previousValue: string[], newValue: string[]) => string[]
	label?: string
	name: string
	onChange?: (value: string[]) => void
	options: (SelectOption | SelectOptionGroup)[]
	required?: boolean
	selectAllLabel?: string
}

export const MultiSelectField = ({
	disabled,
	disabledHint,
	interceptChange,
	label,
	name,
	onChange,
	options,
	required,
	selectAllLabel,
}: MultiSelectFieldProps) => {
	const { control } = useFormContext()
	const { formatMessage } = useIntl()
	const { field, fieldState } = useController({
		control,
		name,
		rules: {
			validate: (value) => !required || value.length > 0 || formatMessage(formMessages.required),
		},
	})

	const handleUpdate = useCallback(
		(value: string[]) => {
			const newValue = interceptChange?.(field.value || [], value) ?? value
			field.onChange(newValue)
			onChange?.(newValue)
		},
		[field, interceptChange, onChange],
	)

	const { ref: fieldRef, value: fieldValue } = field

	return (
		<MultiSelect
			data-testid={`multiselect-field_${name}`}
			name={name}
			label={label}
			options={options}
			ref={fieldRef}
			disabled={disabled}
			disabledHint={disabledHint}
			errorMessage={fieldState.error?.message}
			required={required}
			selectAllLabel={selectAllLabel}
			onChange={handleUpdate}
			value={fieldValue || []}
		/>
	)
}
